import React, { useEffect, useMemo, useState } from 'react';
import { addDays, addHours, format, startOfHour } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  ListenTarget,
  ListeningConfig,
  ListeningSchedule as Schedule,
  putListeningSchedule,
} from '../../api/socialListening';
import { inputDarkSx, neutralChipSx, paperSx } from './styles';

export const INTERVAL_OPTIONS = [
  { minutes: 15, label: 'Every 15 min' },
  { minutes: 30, label: 'Every 30 min' },
  { minutes: 60, label: 'Hourly' },
  { minutes: 120, label: 'Every 2 h' },
  { minutes: 180, label: 'Every 3 h' },
  { minutes: 360, label: 'Every 6 h' },
  { minutes: 720, label: 'Every 12 h' },
  { minutes: 1440, label: 'Daily' },
];

// Apidirect pay-as-you-go price per fetch (1 page) — keep in sync with apidirect.io pricing.
const PRICE_PER_FETCH: Record<string, number> = {
  x: 0.006,
  instagram: 0.006,
  tiktok: 0.006,
  facebook: 0.008,
};

const LEGACY_SCHEDULE: Schedule = { windows: [], defaultIntervalMinutes: 60 };

type Row = { key: number; start: string; end: string; intervalMinutes: number };

const LOCAL_INPUT = "yyyy-MM-dd'T'HH:mm";
const toLocalInput = (d: Date | string) => format(new Date(d), LOCAL_INPUT);

export function intervalLabel(minutes: number): string {
  if (minutes <= 0) return 'Off';
  return INTERVAL_OPTIONS.find((o) => o.minutes === minutes)?.label ?? `Every ${minutes} min`;
}

function formatUSD(v: number): string {
  return v < 10 ? `$${v.toFixed(2)}` : `$${Math.round(v)}`;
}

function rowError(r: Row): string | null {
  if (!r.start || !r.end) return 'Set both dates';
  if (new Date(r.end) <= new Date(r.start)) return 'End must be after start';
  return null;
}

type Props = {
  profileId: string;
  config: ListeningConfig | null;
  targets: ListenTarget[];
  readOnly?: boolean;
  onSaved: (cfg: ListeningConfig) => void;
};

const ListeningSchedule: React.FC<Props> = ({ profileId, config, targets, readOnly = false, onSaved }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [defaultInterval, setDefaultInterval] = useState(60);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState(1);

  // Reset the editor whenever the saved schedule changes (load, save, profile switch).
  const savedKey = JSON.stringify(config?.schedule ?? LEGACY_SCHEDULE);
  useEffect(() => {
    const s = JSON.parse(savedKey) as Schedule;
    setRows(
      s.windows.map((w, i) => ({
        key: i,
        start: toLocalInput(w.start),
        end: toLocalInput(w.end),
        intervalMinutes: w.intervalMinutes,
      }))
    );
    setNextKey(s.windows.length);
    setDefaultInterval(s.defaultIntervalMinutes);
    setDirty(false);
  }, [savedKey, profileId]);

  const enabledTargets = useMemo(() => targets.filter((t) => t.enabled), [targets]);
  const fetchesPerPoll = enabledTargets.reduce((n, t) => n + t.platforms.length, 0);
  const costPerPoll = enabledTargets.reduce(
    (sum, t) => sum + (t.platforms as string[]).reduce((s: number, p) => s + (PRICE_PER_FETCH[p] ?? 0.006), 0),
    0
  );

  const update = (key: number, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const addRow = () => {
    // Default: start at the next full hour (or right after the last period), one week long, hourly.
    const last = rows[rows.length - 1];
    const start = last && last.end ? new Date(last.end) : startOfHour(addHours(new Date(), 1));
    setRows((rs) => [
      ...rs,
      { key: nextKey, start: toLocalInput(start), end: toLocalInput(addDays(start, 7)), intervalMinutes: 60 },
    ]);
    setNextKey((k) => k + 1);
    setDirty(true);
  };

  const removeRow = (key: number) => {
    setRows((rs) => rs.filter((r) => r.key !== key));
    setDirty(true);
  };

  const invalid = rows.some((r) => rowError(r) !== null);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const cfg = await putListeningSchedule(profileId, {
        windows: rows.map((r) => ({
          start: new Date(r.start).toISOString(),
          end: new Date(r.end).toISOString(),
          intervalMinutes: r.intervalMinutes,
        })),
        defaultIntervalMinutes: defaultInterval,
      });
      onSaved(cfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const now = new Date();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Paper sx={{ ...paperSx, mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#fafafa' }}>
          Schedule
        </Typography>
        <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
          Poll more often during the periods you care about. Overlapping periods use the most frequent interval.
          Times are in your timezone ({timeZone}).
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {rows.length === 0 && (
          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
            No periods — the setting below applies all the time.
          </Typography>
        )}
        {rows.map((r) => {
          const err = rowError(r);
          const start = new Date(r.start);
          const end = new Date(r.end);
          const active = !err && start <= now && now < end;
          const past = !err && end <= now;
          const polls = err ? 0 : (end.getTime() - start.getTime()) / 60000 / r.intervalMinutes;
          return (
            <Stack
              key={r.key}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
              sx={{ opacity: past ? 0.55 : 1 }}
            >
              <TextField
                label="From"
                type="datetime-local"
                size="small"
                value={r.start}
                disabled={readOnly}
                onChange={(e) => update(r.key, { start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ ...inputDarkSx, minWidth: 210 }}
              />
              <TextField
                label="To"
                type="datetime-local"
                size="small"
                value={r.end}
                disabled={readOnly}
                onChange={(e) => update(r.key, { end: e.target.value })}
                error={Boolean(err)}
                helperText={err ?? undefined}
                InputLabelProps={{ shrink: true }}
                sx={{ ...inputDarkSx, minWidth: 210 }}
              />
              <FormControl size="small" sx={{ ...inputDarkSx, minWidth: 160 }}>
                <InputLabel>Poll</InputLabel>
                <Select
                  label="Poll"
                  value={r.intervalMinutes}
                  disabled={readOnly}
                  onChange={(e) => update(r.key, { intervalMinutes: Number(e.target.value) })}
                >
                  {INTERVAL_OPTIONS.map((o) => (
                    <MenuItem key={o.minutes} value={o.minutes}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" sx={{ color: '#a1a1aa', minWidth: 150 }}>
                {err ? '' : `≈ ${Math.ceil(polls)} polls · ${formatUSD(polls * costPerPoll)}`}
              </Typography>
              {active && <Chip label="Active now" color="success" size="small" />}
              {past && <Chip label="Ended" size="small" sx={neutralChipSx} />}
              {!readOnly && (
                <IconButton aria-label="Remove period" onClick={() => removeRow(r.key)} sx={{ color: '#a1a1aa' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          );
        })}

        {!readOnly && (
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ color: '#a5b4fc' }}>
              Add period
            </Button>
          </Box>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ ...inputDarkSx, minWidth: 220 }}>
            <InputLabel>{rows.length ? 'Outside these periods' : 'Poll'}</InputLabel>
            <Select
              label={rows.length ? 'Outside these periods' : 'Poll'}
              value={defaultInterval}
              disabled={readOnly}
              onChange={(e) => {
                setDefaultInterval(Number(e.target.value));
                setDirty(true);
              }}
            >
              <MenuItem value={0}>Off</MenuItem>
              {INTERVAL_OPTIONS.map((o) => (
                <MenuItem key={o.minutes} value={o.minutes}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
            {defaultInterval > 0 ? `≈ ${formatUSD((1440 / defaultInterval) * costPerPoll)} / day` : 'No polling'}
          </Typography>
        </Stack>

        <Typography variant="caption" sx={{ color: '#71717a' }}>
          Each poll = {fetchesPerPoll} fetches ({enabledTargets.length} enabled targets × their platforms) ≈{' '}
          {formatUSD(costPerPoll)} at Apidirect pay-as-you-go prices.
        </Typography>

        {!readOnly && (
          <Box>
            <Button
              variant="contained"
              disabled={!dirty || invalid || saving}
              onClick={() => void onSave()}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
            >
              {saving ? 'Saving…' : 'Save schedule'}
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default ListeningSchedule;
